import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments';
import { TaskFilterService } from './task-filter.service';

export interface TotalCatchesV2MobileItemModel {
  totalCatch?: number;
  name?: string;
  moduleName?: string;
}

export interface TotalCatchesV2MobileModel {
  totalCatches?: number;
  catches: TotalCatchesV2MobileItemModel[];
}

export interface VisitAreasModulesModuleItemModel {
  name?: string;
  label?: string;
  value?: boolean;
  color?: string;
  contractPartId?: number;
}

export interface VisitAreasModulesModel {
  name?: string;
  visitId?: number;
  modules?: VisitAreasModulesModuleItemModel[];
}

@Injectable({
  providedIn: 'root',
})
export class VisitService {
  constructor(
    private http: HttpClient,
    private taskFilterService: TaskFilterService
  ) {}

  public sendEndVisitDetails(endVisitDetails: any): Observable<void> {
    const createTime = new Date().toLocaleString('sv').replace(' ', 'T');
    endVisitDetails.createTime = createTime;
    const url = `${environment.apiHost}/mobile/end-visit`;
    return this.http.post<void>(url, endVisitDetails);
  }

  public updateField(
    table: string,
    id: number,
    value: string,
    property: string,
    id2?: number
  ): Observable<void> {
    const visitFieldDetails = {
      table,
      id,
      id2,
      value,
      property,
    };
    const url = `${environment.apiHost}/mobile/update-field`;
    return this.http.post<void>(url, visitFieldDetails);
  }
  public updateOfficeMemo(
    visitId: number,
    memo: string,
    memoPerson: number
  ): Observable<void> {
    const url = `${environment.apiHost}/mobile/update-office-memo`;
    return this.http.patch<void>(url, { visitId, memo, memoPerson });
  }
  public sendSignature(
    visitId: number,
    signature: string,
    signatureName: string,
    signatureFirstName: string,
    signatureTitle: string,
    signatureDate: string
  ): Observable<void> {
    const url = `${environment.apiHost}/mobile/send-signature`;
    return this.http.post<void>(url, {
      visitId,
      signature,
      signatureName,
      signatureDate,
      signatureFirstName,
      signatureTitle,
    });
  }

  public loginAsCustomer(companyId: number): Observable<{ id: number | null }> {
    const url = `${environment.apiHost}/mobile/customer/loginas`;

    return this.http.post<{ id: number | null }>(url, { companyId });
  }

  /**
   * Check if any other visit (not the current one) is currently started
   *
   * @param allVisitsForDay Array of all visits for the current day
   * @param currentVisitId ID of the current visit
   * @returns true if another visit has technicianStarted = true
   */
  public isAnotherVisitStarted(
    allVisitsForDay: any[],
    currentVisitId: number
  ): boolean {
    if (!allVisitsForDay || allVisitsForDay.length === 0) {
      return false;
    }

    // Check if any other visit (not the current one) has technicianStarted = true
    return allVisitsForDay.some(
      (visit) =>
        visit.visitId.toString() !== currentVisitId.toString() &&
        visit.technicianStarted === true
    );
  }

  /**
   * Check if editing should be disabled for a visit
   *
   * @param visit The current visit object
   * @param allVisitsForDay Array of all visits for the current day
   * @param currentVisitId ID of the current visit
   * @returns true if editing should be disabled
   */
  public isStartingEditingDisabled(
    visit: any,
    allVisitsForDay: any[],
    currentVisitId: number
  ): boolean {
    // If LastWeek filter is active, disable editing
    if (this.taskFilterService.getLastWeek()) {
      return true;
    }
    // If technicianStarted is true, always allow editing
    if (visit?.technicianStarted === true) {
      return false;
    }

    // Check if visit is completed and not reopened
    const isCompletedAndNotReopened =
      visit?.isCompleted === true && visit?.isReopened === false;

    // Check if another visit is active
    const isAnotherVisitActive = this.isAnotherVisitStarted(
      allVisitsForDay,
      currentVisitId
    );
    return isCompletedAndNotReopened || isAnotherVisitActive || visit?.paused;
  }

  public isEditingDisabled(visit: any) {
    if (this.taskFilterService.getLastWeek()) {
      return true;
    }

    if (visit?.technicianStarted === false) {
      return true;
    }
    if (visit?.paused) {
      return true;
    }
    return false;
  }

  /**
   * Get total catches for a visit
   *
   * @param visitId The ID of the visit
   * @returns Observable with the total catches count
   */
  public getTotalCatches(visitId: number): Observable<number> {
    const url = `${environment.apiHost}/mobile/visit/total-catches`;
    return this.http.get<number>(url, {
      params: { visitId: visitId.toString() },
    });
  }

  /**
   * Get total catches v2 for a visit
   *
   * @param visitId The ID of the visit
   * @returns Observable with the total catches v2 data structure
   */
  public getTotalCatchesV2(
    visitId: number
  ): Observable<TotalCatchesV2MobileModel> {
    const url = `${environment.apiHost}/mobile/visit/total-catches-v2`;
    return this.http.get<TotalCatchesV2MobileModel>(url, {
      params: { visitId: visitId.toString() },
    });
  }

  /**
   * Get areas and modules for a visit
   *
   * @param visitId The ID of the visit
   * @returns Observable with the areas and modules data structure
   */
  public getVisitAreasModules(
    visitId: number
  ): Observable<VisitAreasModulesModel[]> {
    const url = `${environment.apiHost}/v2/visits/details/areas-modules`;
    return this.http.get<VisitAreasModulesModel[]>(url, {
      params: { visitId: visitId.toString() },
    });
  }
}
